import React, { useCallback, useEffect, useRef, useState } from "react";

import "../../css/chat-funnel.css";

/**
 * ChatPreviewContainer - WhatsApp-inspired chat funnel preview component
 *
 * Recreates the chat-funnel UI with:
 * - Chat bubble layout (user/bot messages)
 * - Dynamic message rendering
 * - Typing indicator
 * - Progress bar
 * - All input types from chat-funnel.js
 */
const ChatPreviewContainer = ({
    steps = [],
    templateFields = {},
    selectedStepId,
    onStepSelect,
    config = {},
}) => {
    // State Management
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [userResponses, setUserResponses] = useState({});
    const [messages, setMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [carouselStates, setCarouselStates] = useState({}); // stepId -> currentSlide
    const [processingState, setProcessingState] = useState({
        active: false,
        taskIndex: 0,
        progress: 0,
    });
    const [inputValues, setInputValues] = useState({}); // For text inputs
    const [sliderStates, setSliderStates] = useState({}); // For slider selected indices
    const [carouselTouchState, setCarouselTouchState] = useState({}); // For carousel swipe handling
    const [rangeValues, setRangeValues] = useState({}); // For range inputs
    const [otpValues, setOtpValues] = useState({}); // For OTP inputs

    // Refs
    const messagesAreaRef = useRef(null);
    const messagesWrapperRef = useRef(null);
    const objectUrlCacheRef = useRef(new Map());
    const renderedStepsRef = useRef(new Set()); // Track which steps have been rendered

    // Default configuration
    const defaultConfig = {
        colors: {
            primary: "#25D366",
            primaryDark: "#1DA851",
            secondary: "#128C7E",
            background: "#E5DDD5",
            headerBg: "#075E54",
        },
        texts: {
            botName: "Chat Funnel Bot",
            botStatus: "Online",
            sendButton: "➤",
            continueButton: "Continue",
            completionTitle: "Thank You!",
            completionText: "Your responses have been recorded.",
            skipButton: "Skip",
            skippedMessage: "⏭ Skipped",
            optionalBadge: "Optional",
            inputPlaceholder: "Type your answer...",
        },
        timing: {
            typingDelay: 1000,
            renderDelay: 300,
            scrollDelay: 150,
            nextStepDelay: 500,
        },
    };

    const finalConfig = {
        colors: { ...defaultConfig.colors, ...(config.colors || {}) },
        texts: { ...defaultConfig.texts, ...(config.texts || {}) },
        timing: { ...defaultConfig.timing, ...(config.timing || {}) },
    };

    useEffect(() => {
        const cache = objectUrlCacheRef.current;
        return () => {
            cache.forEach((url) => URL.revokeObjectURL(url));
            cache.clear();
        };
    }, []);

    // Reset when steps change
    useEffect(() => {
        objectUrlCacheRef.current.forEach((url) => URL.revokeObjectURL(url));
        objectUrlCacheRef.current.clear();
        renderedStepsRef.current.clear(); // Clear rendered steps tracking
        setCurrentStepIndex(0);
        setUserResponses({});
        setMessages([]);
        setIsTyping(false);
        setIsCompleted(false);
        setCarouselStates({});
        setProcessingState({ active: false, taskIndex: 0, progress: 0 });
        setInputValues({});
        setSliderStates({});
        setCarouselTouchState({});
        setRangeValues({});
        setOtpValues({});

        if (steps.length > 0) {
            setTimeout(() => {
                renderStep(steps[0], 0);
            }, finalConfig.timing.typingDelay);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [steps, finalConfig.timing.typingDelay]);

    // Utility Functions
    const getObjectUrl = useCallback((file) => {
        if (!file || typeof URL === "undefined") return null;
        if (objectUrlCacheRef.current.has(file)) {
            return objectUrlCacheRef.current.get(file);
        }
        const url = URL.createObjectURL(file);
        objectUrlCacheRef.current.set(file, url);
        return url;
    }, []);

    const resolveMediaSrc = useCallback(
        (media, visited) => {
            if (!media) return null;

            const seen = visited ?? new Set();

            if (Array.isArray(media)) {
                for (const item of media) {
                    const resolved = resolveMediaSrc(item, seen);
                    if (resolved) return resolved;
                }
                return null;
            }

            if (typeof FileList !== "undefined" && media instanceof FileList) {
                return resolveMediaSrc(Array.from(media), seen);
            }

            if (typeof media === "string") {
                const trimmed = media.trim();
                if (!trimmed) return null;
                return trimmed;
            }

            if (typeof File !== "undefined" && media instanceof File) {
                return getObjectUrl(media);
            }

            if (
                typeof Blob !== "undefined" &&
                media instanceof Blob &&
                !(media instanceof File)
            ) {
                return getObjectUrl(media);
            }

            if (typeof media === "object") {
                if (seen.has(media)) return null;
                seen.add(media);

                const candidateKeys = [
                    "image",
                    "file",
                    "preview",
                    "data",
                    "media",
                    "thumbnail",
                    "thumb",
                    "url",
                    "image_url",
                    "src",
                    "path",
                    "download_url",
                    "link",
                    "href",
                    "value",
                    "original",
                    "origin",
                    "full",
                    "asset",
                    "attributes",
                ];

                for (const key of candidateKeys) {
                    if (
                        Object.prototype.hasOwnProperty.call(media, key) &&
                        media[key] !== media
                    ) {
                        const resolved = resolveMediaSrc(media[key], seen);
                        if (resolved) return resolved;
                    }
                }

                for (const value of Object.values(media)) {
                    if (!value || value === media) continue;
                    const resolved = resolveMediaSrc(value, seen);
                    if (resolved) return resolved;
                }
            }

            return null;
        },
        [getObjectUrl],
    );

    const getCurrentTime = () => {
        const now = new Date();
        return `${now.getHours().toString().padStart(2, "0")}:${now
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;
    };

    const addBotMessage = useCallback((step, stepIndex = null) => {
        if (!step) return;

        // Get step ID to check for duplicates
        // Use step.id or step.step_id if available, otherwise use stepIndex as fallback
        const stepId = step.id || step.step_id;
        const uniqueId =
            stepId ||
            (stepIndex !== null
                ? `step-${stepIndex}`
                : `step-${step.type}-${step.title || "unknown"}`);

        // Check if this step has already been rendered
        if (renderedStepsRef.current.has(uniqueId)) {
            return; // Skip if already rendered
        }

        // Mark this step as rendered
        renderedStepsRef.current.add(uniqueId);

        const message = {
            id: `bot-${Date.now()}-${uniqueId}`,
            type: "bot",
            step: step,
            timestamp: getCurrentTime(),
        };
        setMessages((prev) => [...prev, message]);
    }, []);

    const addUserMessage = useCallback((text) => {
        const message = {
            id: `user-${Date.now()}`,
            type: "user",
            text: text,
            timestamp: getCurrentTime(),
        };
        setMessages((prev) => [...prev, message]);
    }, []);

    const handleResponse = (stepId, value) => {
        setUserResponses((prev) => ({
            ...prev,
            [stepId]: value,
        }));
    };

    const scrollToBottom = useCallback(() => {
        if (messagesAreaRef.current) {
            setTimeout(() => {
                messagesAreaRef.current.scrollTop =
                    messagesAreaRef.current?.scrollHeight;
            }, finalConfig.timing.scrollDelay);
        }
    }, [finalConfig.timing.scrollDelay]);

    // Scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping, scrollToBottom]);

    // Render Step Functions
    const renderStep = useCallback(
        (step, stepIndex = null) => {
            if (!step) return;

            setIsTyping(true);

            setTimeout(() => {
                setIsTyping(false);
                addBotMessage(step, stepIndex);
            }, finalConfig.timing.typingDelay);
        },
        [finalConfig.timing.typingDelay, addBotMessage],
    );

    const completeFlow = useCallback(() => {
        setIsCompleted(true);
        setIsTyping(true);

        setTimeout(() => {
            setIsTyping(false);
            const completionMessage = {
                id: `completion-${Date.now()}`,
                type: "completion",
                timestamp: getCurrentTime(),
            };
            setMessages((prev) => [...prev, completionMessage]);

            // Call completion callback
            if (config.onComplete) {
                config.onComplete(userResponses);
            }
        }, finalConfig.timing.typingDelay);
    }, [finalConfig.timing.typingDelay, config, userResponses]);

    const nextStep = useCallback(() => {
        setIsTyping(false);

        if (currentStepIndex < steps.length - 1) {
            const nextIndex = currentStepIndex + 1;
            setCurrentStepIndex(nextIndex);

            setTimeout(() => {
                renderStep(steps[nextIndex], nextIndex);
            }, finalConfig.timing.nextStepDelay);
        } else {
            completeFlow();
        }
    }, [
        currentStepIndex,
        steps,
        finalConfig.timing.nextStepDelay,
        renderStep,
        completeFlow,
    ]);

    // Progress calculation
    const progress = isCompleted
        ? 100
        : steps.length > 0
          ? (currentStepIndex / steps.length) * 100
          : 0;

    // Note: Initialization is handled by the useEffect that watches [steps] above
    // This prevents duplicate bot messages on mount

    // Normalize step type
    const normalizeType = (type) => {
        switch (type) {
            case "radio-button":
                return "radio";
            case "star_rating":
                return "rating";
            case "nps_rating":
                return "nps";
            case "range_slider":
                return "range";
            case "categorical_slider":
                return "slider";
            case "image_carousel":
                return "carousel";
            case "grid_selector":
                return "grid_selector";
            case "grid_selector_multiple":
                return "grid_selector_multiple";
            case "file_upload":
                return "file";
            case "processing_step":
                return "processing";
            default:
                return type;
        }
    };

    const currentStep = steps[currentStepIndex];

    // Render Input Components
    const renderButtonInput = (step) => {
        return (
            <div className="input-area-chat">
                <button
                    className="chat-button"
                    onClick={() => {
                        handleResponse(step.id || step.step_id, "clicked");
                        addUserMessage(
                            step.attributes?.button_text ||
                                finalConfig.texts.continueButton,
                        );
                        nextStep();
                    }}
                    style={{
                        background:
                            step.attributes?.button_color ||
                            finalConfig.colors.primary,
                        color: step.attributes?.button_text_color || "#ffffff",
                    }}
                >
                    {step.attributes?.button_text ||
                        finalConfig.texts.continueButton}
                </button>
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(step.id || step.step_id, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    const renderMultiselectInput = (step) => {
        const stepId = step.id || step.step_id;
        const selectedOptions = new Set(userResponses[stepId] || []);

        const toggleOption = (option) => {
            const newSet = new Set(selectedOptions);
            if (newSet.has(option)) {
                newSet.delete(option);
            } else {
                newSet.add(option);
            }
            handleResponse(stepId, Array.from(newSet));
        };

        return (
            <div className="input-area-chat">
                <div className="multiselect-container">
                    {step.attributes?.options?.map((option, index) => {
                        const isSelected = selectedOptions.has(option);
                        return (
                            <div
                                key={index}
                                className={`multiselect-option ${
                                    isSelected ? "selected" : ""
                                }`}
                                onClick={() => toggleOption(option)}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    readOnly
                                />
                                <label>{option}</label>
                            </div>
                        );
                    })}
                </div>
                <button
                    className="chat-button multiselect-submit"
                    onClick={() => {
                        if (selectedOptions.size > 0) {
                            const response = Array.from(selectedOptions);
                            addUserMessage(response.join(", "));
                            nextStep();
                        }
                    }}
                    disabled={selectedOptions.size === 0}
                >
                    {finalConfig.texts.continueButton}
                </button>
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(stepId, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    const renderRadioInput = (step) => {
        const stepId = step.id || step.step_id;
        const selectedOption = userResponses[stepId];

        return (
            <div className="input-area-chat">
                <div className="multiselect-container">
                    {step.attributes?.options?.map((option, index) => {
                        const isSelected = selectedOption === option;
                        return (
                            <div
                                key={index}
                                className={`multiselect-option ${
                                    isSelected ? "selected" : ""
                                }`}
                                onClick={() => handleResponse(stepId, option)}
                            >
                                <input
                                    type="radio"
                                    name={`radio-${stepId}`}
                                    checked={isSelected}
                                    readOnly
                                />
                                <label>{option}</label>
                            </div>
                        );
                    })}
                </div>
                <button
                    className="chat-button multiselect-submit"
                    onClick={() => {
                        if (selectedOption) {
                            addUserMessage(selectedOption);
                            nextStep();
                        }
                    }}
                    disabled={!selectedOption}
                >
                    {finalConfig.texts.continueButton}
                </button>
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(stepId, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    const renderSliderInput = (step) => {
        const stepId = step.id || step.step_id;
        const labels = [
            step.attributes?.min || "Min",
            step.attributes?.mid || "Mid",
            step.attributes?.max || "Max",
        ];
        const currentValue =
            userResponses[stepId] !== undefined
                ? labels.indexOf(userResponses[stepId])
                : labels.length > 1
                  ? 1
                  : 0;

        const selectedIndex =
            sliderStates[stepId] !== undefined
                ? sliderStates[stepId]
                : currentValue >= 0
                  ? currentValue
                  : labels.length > 1
                    ? 1
                    : 0;

        const handleLabelClick = (index) => {
            setSliderStates((prev) => ({ ...prev, [stepId]: index }));
            handleResponse(stepId, labels[index]);
        };

        const getSliderPosition = (index) => {
            if (index === 0) return "0%";
            if (index === 1) return "50%";
            return "100%";
        };

        return (
            <div className="input-area-chat">
                <div className="slider-container">
                    <div className="slider-labels">
                        {labels.map((label, index) => (
                            <div
                                key={index}
                                className={`slider-label ${
                                    selectedIndex === index ? "active" : ""
                                }`}
                                onClick={() => handleLabelClick(index)}
                            >
                                {label}
                            </div>
                        ))}
                    </div>
                    <div className="slider-track">
                        <div
                            className="slider-fill"
                            style={{ width: getSliderPosition(selectedIndex) }}
                        />
                        <div
                            className="slider-thumb"
                            style={{ left: getSliderPosition(selectedIndex) }}
                        />
                    </div>
                    <button
                        className="chat-button slider-submit"
                        onClick={() => {
                            addUserMessage(labels[selectedIndex]);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.continueButton}
                    </button>
                    {step.optional && (
                        <button
                            className="skip-button"
                            onClick={() => {
                                handleResponse(stepId, "skipped");
                                addUserMessage(
                                    finalConfig.texts.skippedMessage,
                                );
                                nextStep();
                            }}
                        >
                            {finalConfig.texts.skipButton}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderTextInput = (step) => {
        const stepId = step.id || step.step_id;
        const inputValue = inputValues[stepId] || "";

        const handleSubmit = () => {
            const value = inputValue.trim();
            if (value) {
                handleResponse(stepId, value);
                addUserMessage(value);
                setInputValues((prev) => ({ ...prev, [stepId]: "" }));
                nextStep();
            }
        };

        return (
            <div className="input-area-chat">
                <div className="input-container">
                    <input
                        type={
                            step.attributes?.placeholder?.includes("email")
                                ? "email"
                                : step.attributes?.input_type === "number"
                                  ? "number"
                                  : "text"
                        }
                        className="chat-input"
                        placeholder={
                            step.attributes?.placeholder ||
                            finalConfig.texts.inputPlaceholder
                        }
                        value={inputValue}
                        onChange={(e) => {
                            setInputValues((prev) => ({
                                ...prev,
                                [stepId]: e.target.value,
                            }));
                        }}
                        onKeyPress={(e) => {
                            if (e.key === "Enter") handleSubmit();
                        }}
                    />
                    <button
                        className="input-submit"
                        onClick={handleSubmit}
                        disabled={!inputValue.trim()}
                    >
                        {finalConfig.texts.sendButton}
                    </button>
                </div>
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(stepId, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    const renderCarouselInput = (step) => {
        const stepId = step.id || step.step_id;
        const rawImages = Array.isArray(step.attributes?.images)
            ? step.attributes.images
            : [];
        const slides = rawImages
            .map((imgData) => {
                const src =
                    resolveMediaSrc(imgData?.image) ||
                    resolveMediaSrc(imgData?.file) ||
                    resolveMediaSrc(imgData?.url) ||
                    resolveMediaSrc(imgData?.image_url) ||
                    resolveMediaSrc(imgData);

                if (!src) return null;

                return {
                    src,
                    caption:
                        imgData?.caption ||
                        imgData?.title ||
                        imgData?.text ||
                        "",
                };
            })
            .filter(Boolean);
        const currentSlide = carouselStates[stepId] || 0;
        const safeSlideIndex =
            slides.length === 0
                ? 0
                : Math.min(Math.max(currentSlide, 0), slides.length - 1);

        const handlePrev = () => {
            if (safeSlideIndex > 0) {
                setCarouselStates((prev) => ({
                    ...prev,
                    [stepId]: safeSlideIndex - 1,
                }));
            }
        };

        const handleNext = () => {
            if (safeSlideIndex < slides.length - 1) {
                setCarouselStates((prev) => ({
                    ...prev,
                    [stepId]: safeSlideIndex + 1,
                }));
            }
        };

        const handleDotClick = (index) => {
            if (index < 0 || index >= slides.length) return;
            setCarouselStates((prev) => ({
                ...prev,
                [stepId]: index,
            }));
        };

        // Touch/swipe handling
        const touchState = carouselTouchState[stepId] || {
            start: null,
            end: null,
        };
        const minSwipeDistance = 50;

        const onTouchStart = (e) => {
            setCarouselTouchState((prev) => ({
                ...prev,
                [stepId]: { start: e.targetTouches[0].clientX, end: null },
            }));
        };

        const onTouchMove = (e) => {
            setCarouselTouchState((prev) => ({
                ...prev,
                [stepId]: { ...prev[stepId], end: e.targetTouches[0].clientX },
            }));
        };

        const onTouchEnd = () => {
            if (!touchState.start || !touchState.end) return;
            const distance = touchState.start - touchState.end;
            const isLeftSwipe = distance > minSwipeDistance;
            const isRightSwipe = distance < -minSwipeDistance;

            if (isLeftSwipe && safeSlideIndex < slides.length - 1) {
                handleNext();
            } else if (isRightSwipe && safeSlideIndex > 0) {
                handlePrev();
            }

            setCarouselTouchState((prev) => ({
                ...prev,
                [stepId]: { start: null, end: null },
            }));
        };
        return (
            <div className="input-area-chat">
                {slides.length > 0 && (
                    <div>
                        <div
                            className="carousel-container"
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                        >
                            <div
                                className="carousel-wrapper"
                                style={{
                                    transform: `translateX(-${
                                        safeSlideIndex * 100
                                    }%)`,
                                }}
                            >
                                {slides.map((slide, index) => (
                                    <div key={index} className="carousel-slide">
                                        <img
                                            src={
                                                typeof slide.src === "string"
                                                    ? slide.src
                                                    : URL.createObjectURL(
                                                          slide.src,
                                                      )
                                            }
                                            className="carousel-image"
                                            alt={
                                                slide.caption ||
                                                `Slide ${index + 1}`
                                            }
                                        />
                                        {slide.caption && (
                                            <div className="carousel-caption">
                                                {slide.caption}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="carousel-controls">
                            <button
                                className="carousel-btn"
                                onClick={handlePrev}
                                disabled={safeSlideIndex === 0}
                            >
                                ‹
                            </button>
                            <div className="carousel-dots">
                                {slides.map((_, index) => (
                                    <div
                                        key={index}
                                        className={`carousel-dot ${
                                            index === safeSlideIndex
                                                ? "active"
                                                : ""
                                        }`}
                                        onClick={() => handleDotClick(index)}
                                    />
                                ))}
                            </div>
                            <button
                                className="carousel-btn"
                                onClick={handleNext}
                                disabled={safeSlideIndex >= slides.length - 1}
                            >
                                ›
                            </button>
                        </div>
                        <button
                            className="chat-button"
                            style={{ marginTop: "12px" }}
                            onClick={() => {
                                addUserMessage(
                                    step.attributes?.button_text ||
                                        finalConfig.texts.continueButton,
                                );
                                nextStep();
                            }}
                        >
                            {step.attributes?.button_text ||
                                finalConfig.texts.continueButton}
                        </button>
                        {step.optional && (
                            <button
                                className="skip-button"
                                onClick={() => {
                                    handleResponse(stepId, "skipped");
                                    addUserMessage(
                                        finalConfig.texts.skippedMessage,
                                    );
                                    nextStep();
                                }}
                            >
                                {finalConfig.texts.skipButton}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderRangeInput = (step) => {
        const stepId = step.id || step.step_id;
        const min = Number(step.attributes?.min ?? 0);
        const max = Number(step.attributes?.max ?? 100);
        const stepSize = Number(step.attributes?.step_size ?? 1);
        const unit = step.attributes?.unit || "";
        const currentRange =
            userResponses[stepId] !== undefined
                ? Number(userResponses[stepId])
                : Math.floor((min + max) / 2);

        const clampedValue = Math.max(min, Math.min(max, currentRange));
        const rangeValue =
            rangeValues[stepId] !== undefined
                ? Number(rangeValues[stepId])
                : clampedValue;

        const handleRangeChange = (newValue) => {
            const val = Math.max(min, Math.min(max, newValue));
            setRangeValues((prev) => ({ ...prev, [stepId]: val }));
            handleResponse(stepId, val);
        };

        return (
            <div className="input-area-chat">
                <div className="range-container">
                    <div className="range-label">
                        <span>
                            {step.attributes?.min_label ||
                                step.attributes?.min ||
                                "0"}
                        </span>
                        <span>
                            {step.attributes?.max_label ||
                                step.attributes?.max ||
                                "100"}
                        </span>
                    </div>
                    <div className="range-value">
                        {rangeValue}
                        {unit}
                    </div>
                    <input
                        type="range"
                        className="range-slider"
                        min={min}
                        max={max}
                        step={stepSize}
                        value={rangeValue}
                        onChange={(e) =>
                            handleRangeChange(Number(e.target.value))
                        }
                    />
                    <button
                        className="chat-button"
                        style={{ marginTop: "15px" }}
                        onClick={() => {
                            addUserMessage(`${rangeValue}${unit}`);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.continueButton}
                    </button>
                </div>
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(stepId, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    const renderRatingInput = (step) => {
        const stepId = step.id || step.step_id;
        const maxStars = Number(step.attributes?.number_of_stars || 5);
        const selectedRating = Number(userResponses[stepId] || 0);

        return (
            <div className="input-area-chat">
                <div className="rating-container">
                    <div className="rating-stars">
                        {Array.from({ length: maxStars }).map((_, i) => {
                            const idx = i + 1;
                            const isSelected = idx <= selectedRating;
                            return (
                                <span
                                    key={idx}
                                    className={`rating-star ${
                                        isSelected ? "selected" : ""
                                    }`}
                                    onClick={() => {
                                        handleResponse(stepId, idx);
                                        setTimeout(() => {
                                            addUserMessage(`${idx} ⭐`);
                                            nextStep();
                                        }, 500);
                                    }}
                                >
                                    ★
                                </span>
                            );
                        })}
                    </div>
                </div>
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(stepId, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    const renderNPSInput = (step) => {
        const stepId = step.id || step.step_id;
        const value = Number(userResponses[stepId]);

        return (
            <div className="input-area-chat">
                <div className="nps-container">
                    <div className="nps-scale">
                        {Array.from({ length: 11 }).map((_, i) => (
                            <button
                                key={i}
                                className={`nps-button ${
                                    value === i ? "selected" : ""
                                }`}
                                onClick={() => {
                                    handleResponse(stepId, i);
                                    setTimeout(() => {
                                        addUserMessage(`Score: ${i}/10`);
                                        nextStep();
                                    }, 500);
                                }}
                            >
                                {i}
                            </button>
                        ))}
                    </div>
                    <div className="nps-labels">
                        <span>
                            {step.attributes?.min_label || "Not likely"}
                        </span>
                        <span>
                            {step.attributes?.max_label || "Very likely"}
                        </span>
                    </div>
                </div>
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(stepId, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    const renderOTPInput = (step) => {
        const stepId = step.id || step.step_id;
        const length = Number(
            step.attributes?.otp_length || step.attributes?.length || 6,
        );
        const otpValue = String(userResponses[stepId] || "");
        const currentOtpValues =
            otpValues[stepId] ||
            otpValue.split("").slice(0, length).join("").padEnd(length, "");

        const handleOTPChange = (index, value) => {
            const v = value.replace(/[^0-9A-Za-z]/g, "").slice(0, 1);
            const arr = currentOtpValues.split("");
            arr[index] = v;
            const joined = arr.join("");
            setOtpValues((prev) => ({ ...prev, [stepId]: joined }));
            handleResponse(stepId, joined);

            if (v && index < length - 1) {
                const nextInput = document.getElementById(
                    `otp-${stepId}-${index + 1}`,
                );
                if (nextInput) nextInput.focus();
            }

            if (
                joined.length === length &&
                joined.replace(/\s/g, "").length === length
            ) {
                setTimeout(() => {
                    addUserMessage(joined);
                    nextStep();
                }, 300);
            }
        };

        return (
            <div className="input-area-chat">
                <div className="otp-container">
                    {Array.from({ length }).map((_, idx) => (
                        <input
                            key={idx}
                            id={`otp-${stepId}-${idx}`}
                            type="text"
                            className="otp-input"
                            maxLength={1}
                            value={currentOtpValues[idx] || ""}
                            onChange={(e) =>
                                handleOTPChange(idx, e.target.value)
                            }
                            onKeyDown={(e) => {
                                if (
                                    e.key === "Backspace" &&
                                    !e.target.value &&
                                    idx > 0
                                ) {
                                    const prevInput = document.getElementById(
                                        `otp-${stepId}-${idx - 1}`,
                                    );
                                    if (prevInput) prevInput.focus();
                                }
                            }}
                        />
                    ))}
                </div>
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(stepId, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    const renderFileInput = (step) => {
        const stepId = step.id || step.step_id;
        const fileType = step.attributes?.file_type;
        const accept = Array.isArray(fileType)
            ? fileType
                  .map((ft) =>
                      ft === "image"
                          ? "image/*"
                          : ft === "video"
                            ? "video/*"
                            : ft === "audio"
                              ? "audio/*"
                              : ft === "document"
                                ? ".pdf,.doc,.docx,.xls,.xlsx"
                                : "*/*",
                  )
                  .join(",")
            : fileType === "image"
              ? "image/*"
              : fileType === "video"
                ? "video/*"
                : fileType === "audio"
                  ? "audio/*"
                  : fileType === "document"
                    ? ".pdf,.doc,.docx,.xls,.xlsx"
                    : undefined;

        const handleFileSelect = (file) => {
            if (file) {
                handleResponse(stepId, file.name);
                setTimeout(() => {
                    addUserMessage(`📄 ${file.name}`);
                    nextStep();
                }, 500);
            }
        };

        return (
            <div className="input-area-chat">
                <div className="file-upload-container">
                    <div
                        className="file-upload-area"
                        onClick={() =>
                            document.getElementById(`file-${stepId}`)?.click()
                        }
                    >
                        <div className="file-upload-icon">📎</div>
                        <div className="file-upload-text">
                            Click or drag to upload
                        </div>
                    </div>
                    <input
                        id={`file-${stepId}`}
                        type="file"
                        className="file-input"
                        accept={accept}
                        onChange={(e) => {
                            handleFileSelect(e.target.files?.[0]);
                        }}
                    />
                    {userResponses[stepId] && (
                        <div className="file-selected">
                            Selected: {userResponses[stepId]}
                        </div>
                    )}
                </div>
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(stepId, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    const renderGridSelectorInput = (step) => {
        const stepId = step.id || step.step_id;
        const items = step.attributes?.grid_items || [];
        const multi = normalizeType(step.type) === "grid_selector_multiple";
        const selectedSet = new Set(
            Array.isArray(userResponses[stepId])
                ? userResponses[stepId]
                : userResponses[stepId]
                  ? [userResponses[stepId]]
                  : [],
        );

        const onSelect = (val) => {
            if (multi) {
                const next = new Set(selectedSet);
                if (next.has(val)) next.delete(val);
                else next.add(val);
                handleResponse(stepId, Array.from(next));
            } else {
                handleResponse(stepId, val);
                addUserMessage(val);
                nextStep();
            }
        };

        return (
            <div className="input-area-chat">
                <div className="image-grid-container">
                    {items.map((it, idx) => {
                        const title = it?.title || it?.text || "";
                        const description = it?.description || "";
                        const val = title || idx;
                        const selected = selectedSet.has(val);
                        const imageSrc =
                            resolveMediaSrc(it) ||
                            resolveMediaSrc(it?.media) ||
                            resolveMediaSrc(it?.thumbnail);
                        return (
                            <div
                                key={idx}
                                className={`image-grid-item ${
                                    selected ? "selected" : ""
                                }`}
                                onClick={() => onSelect(val)}
                            >
                                <div className="checkmark">✓</div>
                                {imageSrc && (
                                    <img
                                        src={
                                            typeof imageSrc === "string"
                                                ? imageSrc
                                                : URL.createObjectURL(imageSrc)
                                        }
                                        alt={
                                            title ||
                                            description ||
                                            `item-${idx + 1}`
                                        }
                                    />
                                )}
                                {!!title && (
                                    <div className="image-grid-item-text">
                                        {title}
                                    </div>
                                )}
                                {!!description && (
                                    <div className="image-grid-item-description">
                                        {description}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {multi && (
                    <button
                        className="chat-button"
                        style={{ marginTop: "12px" }}
                        onClick={() => {
                            if (selectedSet.size > 0) {
                                const selected = Array.from(selectedSet);
                                addUserMessage(selected.join(", "));
                                nextStep();
                            }
                        }}
                        disabled={selectedSet.size === 0}
                    >
                        {finalConfig.texts.continueButton}
                    </button>
                )}
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(stepId, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    const renderCheckboxInput = (step) => {
        const stepId = step.id || step.step_id;
        const isChecked = userResponses[stepId] || false;

        return (
            <div className="input-area-chat">
                <div
                    className="multiselect-option"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                        handleResponse(stepId, !isChecked);
                        addUserMessage(
                            !isChecked
                                ? step.attributes?.label || "Checked"
                                : "Unchecked",
                        );
                        nextStep();
                    }}
                >
                    <input type="checkbox" checked={isChecked} readOnly />
                    <label>
                        {step.attributes?.label || "Check this option"}
                    </label>
                </div>
                {step.optional && (
                    <button
                        className="skip-button"
                        onClick={() => {
                            handleResponse(stepId, "skipped");
                            addUserMessage(finalConfig.texts.skippedMessage);
                            nextStep();
                        }}
                    >
                        {finalConfig.texts.skipButton}
                    </button>
                )}
            </div>
        );
    };

    // Handle processing step auto-advance
    useEffect(() => {
        if (!currentStep) return;
        const stepType = normalizeType(currentStep.type);
        if (stepType !== "processing") {
            setProcessingState({ active: false, taskIndex: 0, progress: 0 });
            return;
        }

        const tasks = currentStep.attributes?.processing_steps || [];
        if (!tasks.length) {
            handleResponse(currentStep.id || currentStep.step_id, "completed");
            nextStep();
            return;
        }

        let cancelled = false;
        let idx = 0;
        const runTask = () => {
            if (cancelled || idx >= tasks.length) {
                handleResponse(
                    currentStep.id || currentStep.step_id,
                    "completed",
                );
                nextStep();
                return;
            }
            const duration = Number(tasks[idx]?.duration || 1500);
            const start = Date.now();
            const tick = () => {
                if (cancelled) return;
                const pct = Math.min(
                    100,
                    Math.round(((Date.now() - start) / duration) * 100),
                );
                setProcessingState({
                    active: true,
                    taskIndex: idx,
                    progress: pct,
                });
                if (pct >= 100) {
                    idx += 1;
                    setTimeout(runTask, 200);
                } else {
                    requestAnimationFrame(tick);
                }
            };
            requestAnimationFrame(tick);
        };
        runTask();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep?.id || currentStep?.step_id]);

    const renderProcessingInput = (step) => {
        const tasks = step.attributes?.processing_steps || [];

        return (
            <div className="input-area-chat">
                <div className="processing-container">
                    {tasks.length > 0 && (
                        <div className="processing-title">
                            {step.attributes?.title || "Processing..."}
                        </div>
                    )}
                    {tasks.map((t, i) => (
                        <div key={i} className="processing-item">
                            <div className="processing-label">
                                <span>
                                    {t.step_title || t.label || `Step ${i + 1}`}
                                </span>
                                <span className="processing-percentage">
                                    {processingState.taskIndex > i
                                        ? "100%"
                                        : processingState.taskIndex === i
                                          ? `${processingState.progress}%`
                                          : "0%"}
                                </span>
                            </div>
                            <div className="processing-bar-container">
                                <div
                                    className={`processing-bar ${
                                        processingState.taskIndex === i
                                            ? "active"
                                            : ""
                                    }`}
                                    style={{
                                        width:
                                            processingState.taskIndex > i
                                                ? "100%"
                                                : processingState.taskIndex ===
                                                    i
                                                  ? `${processingState.progress}%`
                                                  : "0%",
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderInputArea = () => {
        if (!currentStep || isCompleted) return null;

        const stepType = normalizeType(currentStep.type);

        switch (stepType) {
            case "button":
                return renderButtonInput(currentStep);
            case "multiselect":
                return renderMultiselectInput(currentStep);
            case "radio":
                return renderRadioInput(currentStep);
            case "slider":
                return renderSliderInput(currentStep);
            case "input":
                return renderTextInput(currentStep);
            case "range":
                return renderRangeInput(currentStep);
            case "rating":
                return renderRatingInput(currentStep);
            case "nps":
                return renderNPSInput(currentStep);
            case "otp":
                return renderOTPInput(currentStep);
            case "file":
                return renderFileInput(currentStep);
            case "carousel":
            case "image":
                return renderCarouselInput(currentStep);
            case "grid_selector":
            case "grid_selector_multiple":
                return renderGridSelectorInput(currentStep);
            case "checkbox":
                return renderCheckboxInput(currentStep);
            case "processing":
                return renderProcessingInput(currentStep);
            default:
                return (
                    <div className="input-area-chat">
                        <div
                            style={{
                                padding: "20px",
                                textAlign: "center",
                                color: "#666",
                            }}
                        >
                            Unsupported step type: {currentStep.type}
                        </div>
                    </div>
                );
        }
    };

    // Render Messages
    const renderMessage = (message) => {
        if (message.type === "bot") {
            const step = message.step;
            const imageSrc =
                resolveMediaSrc(step?.attributes?.image) ||
                resolveMediaSrc(step?.image) ||
                resolveMediaSrc(step?.attributes?.image_url) ||
                resolveMediaSrc(step?.image_url);
            const videoSrc =
                resolveMediaSrc(step?.attributes?.video) ||
                resolveMediaSrc(step?.video) ||
                resolveMediaSrc(step?.attributes?.video_url) ||
                resolveMediaSrc(step?.video_url);
            return (
                <div key={message.id} className="message bot">
                    <div className="message-bubble">
                        {step.title && (
                            <div className="message-title">
                                {step.title}
                                {step.optional && (
                                    <span className="optional-badge">
                                        {finalConfig.texts.optionalBadge}
                                    </span>
                                )}
                            </div>
                        )}
                        {step.description && (
                            <div className="message-description">
                                {step.description}
                            </div>
                        )}
                        {imageSrc && (
                            <div className="image-container">
                                <img
                                    src={
                                        typeof imageSrc === "string"
                                            ? imageSrc
                                            : URL.createObjectURL(imageSrc)
                                    }
                                    className="single-image"
                                    alt={step.title || "Image"}
                                />
                            </div>
                        )}
                        {videoSrc && (
                            <div className="video-container">
                                <video
                                    src={videoSrc}
                                    className="chat-video"
                                    controls
                                    autoPlay={step.attributes?.autoplay}
                                />
                            </div>
                        )}
                        <div
                            className="message-time"
                            style={{ textAlign: "left" }}
                        >
                            {message.timestamp}
                        </div>
                    </div>
                </div>
            );
        } else if (message.type === "user") {
            return (
                <div key={message.id} className="message user">
                    <div className="message-bubble">
                        <div>{message.text}</div>
                        <div className="message-time">
                            {message.timestamp} ✓✓
                        </div>
                    </div>
                </div>
            );
        } else if (message.type === "completion") {
            return (
                <div key={message.id} className="completion-message">
                    <div className="completion-icon">✓</div>
                    <div className="completion-title">
                        {finalConfig.texts.completionTitle}
                    </div>
                    <div className="completion-text">
                        {finalConfig.texts.completionText}
                    </div>
                </div>
            );
        }
        return null;
    };

    // Empty state
    if (steps.length === 0) {
        return (
            <div className="chat-container">
                <div className="chat-header">
                    <div className="chat-avatar">🤖</div>
                    <div className="chat-header-info">
                        <h1>{finalConfig.texts.botName}</h1>
                        <div className="chat-status">
                            {finalConfig.texts.botStatus}
                        </div>
                    </div>
                </div>
                <div
                    className="messages-area"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#666",
                    }}
                >
                    No steps to preview. Add some steps to see the preview.
                </div>
            </div>
        );
    }

    return (
        <div className="chat-container">
            {/* Header */}
            <div className="chat-header">
                <div className="chat-avatar">🤖</div>
                <div className="chat-header-info">
                    <h1>{finalConfig.texts.botName}</h1>
                    <div className="chat-status">
                        {finalConfig.texts.botStatus}
                    </div>
                </div>
            </div>

            {/* Messages Area */}
            <div className="messages-area" ref={messagesAreaRef}>
                <div className="messages-wrapper" ref={messagesWrapperRef}>
                    {messages.map((message) => renderMessage(message))}

                    {/* Typing Indicator */}
                    {isTyping && (
                        <div className="typing-indicator active">
                            <div className="typing-bubble">
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Input Area */}
                {renderInputArea()}
            </div>
        </div>
    );
};

export default ChatPreviewContainer;
